import { KITS } from './runtime/kits';
import { patternDirectives } from '@/llm/styleDna';
import type { StyleBrief } from '@/llm/planTypes';

/**
 * Turning a reference picture into something a web app build can actually use.
 *
 * The plan-first flow derives a `StyleBrief` from an image — sampled hex colours, a mood, a
 * few keywords, and premium UI patterns. A web app cannot take the colours directly: its
 * prompt says "COLOUR COMES FROM THE KIT, NOT FROM YOU" and forbids writing a hex, because a
 * hardcoded colour paints over the chosen kit and breaks dark mode (the tokens change, a hex
 * does not).
 *
 * So the palette is not injected — it CHOOSES. The image's primary colour picks the nearest
 * kit, and the kit carries the colour the way the runtime expects. What a kit cannot express
 * — the mood, the keywords, the patterns — travels as prose instead.
 *
 * Pure, and free of anything that touches a network or a device, so the decision that turns
 * somebody's photo into their app's look is testable on its own.
 */

function rgbOf(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex ?? '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * The kit whose accent sits closest to this colour.
 *
 * Weighted rather than plain Euclidean: the eye is far more sensitive to green than to blue,
 * so an unweighted distance picks kits that look wrong to a person while being "closest" to a
 * computer. These are the usual low-cost weights, which is enough to choose between eight.
 */
export function nearestKitId(hex: string, fallback = 'editorial'): string {
  const target = rgbOf(hex);
  if (!target) return fallback;

  let bestId = fallback;
  let best = Infinity;
  for (const kit of KITS) {
    const c = rgbOf(kit.accent);
    if (!c) continue;
    const d =
      2 * (c.r - target.r) ** 2 + 4 * (c.g - target.g) ** 2 + 3 * (c.b - target.b) ** 2;
    if (d < best) {
      best = d;
      bestId = kit.id;
    }
  }
  return bestId;
}

/**
 * What the kit cannot carry, said to the model in words.
 *
 * Deliberately NOT the palette — that is the kit's job, and repeating it here is how a hex
 * ends up hardcoded in the CSS. Empty when the brief says nothing worth saying, so an
 * imageless build's prompt is byte-for-byte what it was.
 */
export function styleDirectives(brief: StyleBrief | null | undefined): string[] {
  if (!brief) return [];
  const out: string[] = [];

  const mood = (brief.mood ?? '').trim();
  const keywords = (brief.keywords ?? []).map((k) => k.trim()).filter(Boolean);
  if (mood || keywords.length) {
    out.push(
      'THE USER SHOWED A PICTURE OF THE LOOK THEY WANT. Match its feel, not its content:',
      ...(mood ? [`  mood: ${mood}`] : []),
      ...(keywords.length ? [`  style: ${keywords.slice(0, 6).join(', ')}`] : []),
    );
  }

  // Shape, weight and spacing — real choices the kit leaves open.
  if (brief.radius) out.push(`  corners: ${brief.radius}`);
  if (brief.density) out.push(`  spacing: ${brief.density}`);
  if (brief.typeScale) out.push(`  headings: ${brief.typeScale}`);

  const patterns = patternDirectives(brief.patterns ?? []);
  if (patterns) out.push(patterns);

  if (out.length) {
    out.push(
      'The palette is already set by the kit above — do NOT write hex colours to match the',
      'picture. Match it with type, spacing, shape and hierarchy instead.',
      '',
    );
  }
  return out;
}
